import moment from 'moment';
import React, {useEffect, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Paper, Grid, Box, TextField, Button, IconButton } from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingIcon from '@mui/icons-material/Pending';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';

import { getOperations, updateOperation } from './OperationsSlice';
import { getTeam } from '../team/TeamSlice';
import { displayClientName } from '../../utils/Utils';
import Loading from '../../utils/Loading';
import MultiSelect from '../../utils/MultiSelect';
import ServiceChip from '../../utils/ServiceChip';

function UpdateOps () {
    const { ops, isLoading: isLoadingOps } = useSelector((state) =>  state.ops);
    const { team, isLoading: isLoadingTeam } = useSelector((state) => state.team);
    const { opId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (Object.keys(ops).length === 0) {
            dispatch(getOperations());
        }
        if (Object.keys(team).length === 0) {
            dispatch(getTeam());
        }
    }, [ops, team, dispatch]);

    const op = getOperation(ops, opId);
    const [dispatchDriver, setDispatchDriver] = useState(op.dispatch_driver || []);
    const [returnDriver, setReturnDriver] = useState(op.return_driver || []);
    const [remarks, setRemarks] = useState(op.remarks || '');
    const drivers = getDrivers(team);
    const [dispatchKm, setDispatchKm] = useState(parseFloat(op.dispatch_km) || 0);
    const [dispatchFuel, setDispatchFuel] = useState(parseFloat(op.dispatch_fuel) || 0);
    const [returnKm, setReturnKm] = useState(parseFloat(op.return_km) || 0);
    const [returnFuel, setReturnFuel] = useState(parseFloat(op.return_fuel) || 0);

    const handleSubmit = (event) => {
        event.preventDefault();
        const opData = {
            id: op.id,
            dispatch_driver: dispatchDriver,
            return_driver: returnDriver,
            remarks: remarks,
            dispatch_km: dispatchKm,
            dispatch_fuel: dispatchFuel,
            return_km: returnKm,
            return_fuel: returnFuel
        }
        dispatch(updateOperation(opData));
        navigate('/manage/ops')
    };

    const handleDispatchDriver = (event) => {
        const { target: {value}, } = event;
        setDispatchDriver(typeof value === 'string' ? value.split(',') : value)
    };
    const handleReturnDriver = (event) => {
        const { target: {value}, } = event;
        setReturnDriver(typeof value === 'string' ? value.split(',') : value)
    };

    return (
        <Container>
            <Typography variant='h3' align='center' gutterBottom>
                Update operation
            </Typography>
            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                {isLoadingOps || isLoadingTeam ? <Loading /> : null}
            </Box>
            <Paper sx={{padding: 2, marginBottom: '20px'}}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 1 }}>
                    <Typography variant='subtitle2'>
                        Booking details for {op?.booking?.reference || ''}
                    </Typography>
                    {op?.booking?.service_type && <ServiceChip serviceType={op.booking.service_type} />}
                </Box>
                <Typography variant='body1' gutterBottom>
                    {op && Object.keys(op).length > 0 ? primaryListItemText(op) : ''}
                </Typography>
                <Typography variant='body2' gutterBottom>
                    {op && Object.keys(op).length > 0 ? secondaryListItemText(op) : ''}
                </Typography>
                {op && Object.keys(op).length > 0 ? <PaymentStatus op={op} /> : ''}
            </Paper>
            <Paper sx={{padding: 2}}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2} align='center'>
                        <Grid item xs={12} md={6}>
                            <MultiSelect
                                inputLabel={'Dispatch drivers'}
                                values={drivers}
                                valuesState={dispatchDriver}
                                labelFunction={displayClientName}
                                onChange={handleDispatchDriver}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <MultiSelect
                                inputLabel={'Return drivers'}
                                values={drivers}
                                valuesState={returnDriver}
                                labelFunction={displayClientName}
                                onChange={handleReturnDriver}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ marginBottom: 3 }}>
                            <TextField label='Remarks' value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline maxRows={10} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField label={'Dispatch km'} value={dispatchKm} onChange={(e) => setDispatchKm(e.target.value)} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField label={'Dispatch fuel'} value={dispatchFuel} onChange={(e) => setDispatchFuel(e.target.value)} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField label={'Return km'} value={returnKm} onChange={(e) => setReturnKm(e.target.value)} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField label={'Return fuel'} value={returnFuel} onChange={(e) => setReturnFuel(e.target.value)} />
                        </Grid> 
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                            <IconButton aria-label='Images' onClick={() => op?.booking?.reference && navigate(`/manage/bookings/${op.booking.reference}/gallery`)}>
                                <ImageSearchIcon color='info' />
                            </IconButton>
                        </Grid>
                        <Grid item xs={12}>
                            <Button type='submit' variant='contained' endIcon={<UpdateIcon />} >
                                Update
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Container>
    )
}

export default UpdateOps;

export const getOperation = (ops, opId) => {
    if (!ops || !Array.isArray(ops) || ops.length === 0) {
        return {};
    }
    return ops.find((op) => op?.id?.toString() === opId?.toString()) || {};
}

export const getDrivers = (team) => {
    if (!team || !Array.isArray(team) || team.length === 0) {
        return [];
    }
    return team.filter((member) => member?.group?.name === 'Drivers').map(member => member?.user).filter(Boolean);
}

export const primaryListItemText = (op) => {
    if (!op || !op.booking) return '';
    var location = ''
    var opTime = ''
    if (op.operation_due === 'Dispatch') {
        location = op.booking.pickup_location ? op.booking.pickup_location.location : (op.booking.delivery_address || '')
        opTime = op.booking.pickup_datetime ? moment(op.booking.pickup_datetime).format('D-MM-YY HH:mm') : '';
    }
    if (op.operation_due === 'Return') {
        location = op.booking.dropoff_location ? op.booking.dropoff_location.location : (op.booking.collection_address || '')
        opTime = op.booking.dropoff_datetime ? moment(op.booking.dropoff_datetime).format('D-MM-YY HH:mm') : '';
    }
    const vehicleStr = op.booking.vehicle 
        ? `${op.booking.vehicle.registration || ''} ${op.booking.vehicle.model?.vehicle_make || ''} ${op.booking.vehicle.model?.vehicle_model || ''}`.trim()
        : '';
    const clientName = op.booking.client ? displayClientName(op.booking.client) : '';
    return (`${op.operation_due || ''} > ${opTime} | ${location} | ${vehicleStr} | ${clientName}`)
};

export const secondaryListItemText = (op) => {
    if (!op || !op.booking) return '';
    const extras = Array.isArray(op.booking.extras)
        ? op.booking.extras.map((extra) => `| ${extra?.code || extra?.name || extra}`)
        : [];
    const insuranceCode = op.booking.insurance_waiver?.code || '';
    return `${insuranceCode} ${[...extras].join(' ')}`.trim();
}

export const PaymentStatus = ({op}) => {
    if (!op || !op.percent_paid) return null;
    const percentPaid = parseInt(op.percent_paid.slice(0, -1), 10) || 0;
    if (percentPaid <= 0) {
        return (
            <Box sx={{paddingTop: 1, paddingBottom: 1, paddingRight: 1, display: 'flex', alignItems: 'center'}}>
                <WarningAmberIcon color='error' />
                <Typography sx={{ paddingLeft: 1}}>{op.percent_paid} of booking value paid</Typography>
            </Box>
        )
    }
    if (percentPaid < 100) {
        return (
            <Box sx={{paddingTop: 1, paddingBottom: 1, paddingRight: 1, display: 'flex', alignItems: 'center'}}>
                <PendingIcon color='warning' />
                <Typography sx={{ paddingLeft: 1}}>{op.percent_paid} of booking value paid</Typography>
            </Box>
        )
    }
    if (percentPaid >= 100) {
        return (
            <Box sx={{paddingTop: 1, paddingBottom: 1, paddingRight: 1, display: 'flex', alignItems: 'center'}}>
                <CheckCircleOutlineIcon color='success' />
                <Typography sx={{ paddingLeft: 1}}>{op.percent_paid} of booking value paid</Typography>
            </Box>
        )
    }
    return null;
}